import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Btn, Eyebrow } from "../../components/Primitives";
import { api } from "../../lib/api";
import logger from "../../lib/logger";

const RISE_INIT = { opacity: 0, y: 8 };
const RISE_ANIM = { opacity: 1, y: 0 };

function fmtCountdown(expiresAt) {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return "Expired";
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
}

function statusLabel(m) {
    if (m.status === "booked") return "Date confirmed";
    if (m.status === "completed") return "Date complete";
    if (m.status === "dissolved") return "Expired";
    if ((m.proposals || []).length === 0) return "New match — pick your times";
    if ((m.proposals || []).length === 1) return "Waiting on availability";
    if (m.active_date) return "Proposal in flight";
    return "Pick a venue";
}

export default function Matches({ user, onOpenMatch, onBack }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.dating.listMatches(user.session_id);
            setItems(r.items || []);
        } catch (e) {
            logger.error("matches load failed", e);
        }
        setLoading(false);
    }, [user.session_id]);

    useEffect(() => { load(); }, [load]);

    const createTestMatch = async () => {
        setCreating(true);
        try {
            await api.dating.createTestMatch(user.session_id);
            await load();
        } catch (e) { logger.error("test match failed", e); }
        setCreating(false);
    };

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="dating-matches-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="matches-back-btn" onClick={onBack} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">← Back</button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Your dates</span>
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>Date planning</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-[1.1]">
                    <span className="italic">Your</span> matches.
                </h1>
                <p className="mt-2 text-sm text-ink-muted leading-snug">
                    When you match with someone, you have <strong>7 days</strong> to plan a date.
                    No endless chatting. Pick three times that work, find a place, show up.
                </p>
            </div>

            <div className="flex-1 px-6 py-5 space-y-3 overflow-y-auto">
                {loading && <div className="text-ink-muted text-sm text-center py-10">Loading…</div>}

                {!loading && items.length === 0 && !creating && (
                    <div className="text-center py-10 px-4" data-testid="matches-empty">
                        <p className="font-display italic text-xl text-burgundy">No active matches yet.</p>
                        <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                            When we pair you with someone compatible, they'll appear here with a 7-day planning window.
                        </p>
                        <button
                            data-testid="matches-create-test"
                            onClick={createTestMatch}
                            disabled={creating}
                            className="mt-6 inline-block text-[11px] tracking-[0.28em] uppercase text-burgundy underline underline-offset-4 disabled:opacity-50"
                        >
                            {creating ? "Creating…" : "Try the flow with a sample match →"}
                        </button>
                    </div>
                )}

                {!loading && creating && items.length === 0 && (
                    <div className="text-center py-10 px-4" data-testid="matches-creating">
                        <p className="font-display italic text-xl text-burgundy">Spinning up your sample match…</p>
                    </div>
                )}

                {!loading && items.map((m, i) => (
                    <motion.button
                        key={m.id}
                        initial={RISE_INIT}
                        animate={RISE_ANIM}
                        transition={{ delay: i * 0.06 }}
                        onClick={() => onOpenMatch(m.id)}
                        data-testid={`match-card-${m.id}`}
                        className="w-full text-left bg-cream border border-burgundy/15 hover:border-burgundy/40 transition-colors p-4 flex gap-3 items-center"
                    >
                        <div className="w-14 h-14 rounded-full bg-burgundy/10 flex items-center justify-center text-burgundy font-display text-xl">
                            {(m.other_user?.first_name || "?").charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                                <div className="font-display text-xl text-burgundy">{m.other_user?.first_name || "Your match"}</div>
                                {m.other_archetype && (
                                    <div className="text-[10px] tracking-[0.24em] uppercase text-ink-muted truncate">
                                        {m.other_archetype.name}
                                    </div>
                                )}
                            </div>
                            <div className="text-xs text-ink-muted mt-0.5">{statusLabel(m)}</div>
                            {m.status === "planning" && (
                                <div className="text-[10px] tracking-[0.28em] uppercase text-gold-deep mt-1">
                                    {fmtCountdown(m.expires_at)}
                                </div>
                            )}
                        </div>
                        <span className="text-burgundy">→</span>
                    </motion.button>
                ))}

                {!loading && items.length > 0 && (
                    <div className="pt-4">
                        <Btn data-testid="matches-create-another" variant="secondary" onClick={createTestMatch} disabled={creating}>
                            {creating ? "Creating…" : "Add a sample match"}
                        </Btn>
                    </div>
                )}
            </div>
        </div>
    );
}
